import type { OnboardingStep, OnboardingStepGroup } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Progress,
  Skeleton,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, Circle, EyeOff, MinusCircle, Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { STEP_LINKS } from '@/features/onboarding/links';
import {
  onboardingKeys,
  onboardingQueryOptions,
  updateOnboarding,
  type UpdateOnboardingPayload,
} from '@/features/onboarding/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/setup')({ component: SetupPage });

const GROUPS: OnboardingStepGroup[] = ['identity', 'property', 'sales', 'team'];

/** « Mise en route » (ADR-022, D-H46) : liste calculée, liens directs, étapes ignorables, masquage. */
function SetupPage() {
  const { t } = useTranslation(['onboarding', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = session.can('tenant.read');
  const canUpdate = session.can('tenant.update');
  const query = useQuery(onboardingQueryOptions(canRead));

  const mutation = useMutation({
    mutationFn: (payload: UpdateOnboardingPayload) => updateOnboarding(payload),
    onSuccess: async () => {
      toast.success(t('onboarding:toasts.updated'));
      await queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const status = query.data;
  return (
    <Container>
      <PageHeader
        title={t('onboarding:title')}
        subtitle={t('onboarding:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('onboarding:title') },
        ]}
        actions={
          canUpdate && status ? (
            <Button
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ dismissed: status.dismissedAt === null })}
            >
              <EyeOff className="ico" aria-hidden="true" />
              {status.dismissedAt ? t('onboarding:restore') : t('onboarding:dismiss')}
            </Button>
          ) : null
        }
      />

      {query.isPending ? (
        <div className="stack" aria-busy="true">
          <Skeleton height={80} />
          <Skeleton height={240} />
        </div>
      ) : query.isError || !status ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : (
        <div className="stack">
          <Card>
            <CardBody>
              <div className="stack" style={{ gap: 8 }}>
                <Progress
                  value={status.done}
                  max={status.total}
                  tone={status.complete ? 'green' : 'violet'}
                  label={t('onboarding:progress', { done: status.done, total: status.total })}
                  valueText={t('onboarding:progress', { done: status.done, total: status.total })}
                />
                <strong>
                  {t('onboarding:progress', { done: status.done, total: status.total })}
                </strong>
                {status.complete ? (
                  <Notice tone="success">{t('onboarding:complete')}</Notice>
                ) : status.dismissedAt ? (
                  <Notice tone="info">{t('onboarding:hiddenNotice')}</Notice>
                ) : null}
              </div>
            </CardBody>
          </Card>

          {GROUPS.map((group) => {
            const steps = status.steps.filter((step) => step.group === group);
            if (steps.length === 0) return null;
            return (
              <Card key={group}>
                <CardHeader
                  title={t(`onboarding:groups.${group}`)}
                  icon={<Rocket className="ico ico-16" aria-hidden="true" />}
                />
                <CardBody density="flush">
                  <ul className="setup-steps" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {steps.map((step) => (
                      <StepRow
                        key={step.code}
                        step={step}
                        canUpdate={canUpdate}
                        pending={mutation.isPending}
                        onSkip={() => mutation.mutate({ skip: [step.code] })}
                        onUnskip={() => mutation.mutate({ unskip: [step.code] })}
                      />
                    ))}
                  </ul>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </Container>
  );
}

function StepRow({
  step,
  canUpdate,
  pending,
  onSkip,
  onUnskip,
}: {
  step: OnboardingStep;
  canUpdate: boolean;
  pending: boolean;
  onSkip: () => void;
  onUnskip: () => void;
}) {
  const { t } = useTranslation(['onboarding', 'common']);
  const link = STEP_LINKS[step.code];
  const tone = step.status === 'DONE' ? 'green' : step.status === 'SKIPPED' ? 'neutral' : 'amber';
  const Icon = step.status === 'DONE' ? Check : step.status === 'SKIPPED' ? MinusCircle : Circle;
  return (
    <li
      className="row row--wrap"
      data-testid={`onboarding-step-${step.code}`}
      style={{
        gap: 12,
        alignItems: 'center',
        padding: '12px 16px',
        borderTop: '1px solid var(--color-border, #e5e7eb)',
      }}
    >
      <Icon className="ico ico-16" aria-hidden="true" />
      <div className="stack" style={{ gap: 2, flex: 1, minWidth: 220 }}>
        <span className="row" style={{ gap: 8, alignItems: 'center' }}>
          <strong>{t(`onboarding:steps.${step.code}.title`)}</strong>
          <Badge tone={tone}>{t(`onboarding:status.${step.status}`)}</Badge>
          {step.count !== null && step.count > 0 ? (
            <span className="t-12 muted">{t('onboarding:count', { count: step.count })}</span>
          ) : null}
        </span>
        <span className="t-12 muted">{t(`onboarding:steps.${step.code}.description`)}</span>
      </div>
      <span className="row" style={{ gap: 4 }}>
        <Button size="sm" variant={step.status === 'TODO' ? 'primary' : 'secondary'} asChild>
          <Link to={link.to} search={link.tab ? ({ tab: link.tab } as never) : undefined}>
            {t('onboarding:actions.configure')}
          </Link>
        </Button>
        {canUpdate && step.skippable && step.status === 'TODO' ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={onSkip}>
            {t('onboarding:actions.skip')}
          </Button>
        ) : canUpdate && step.status === 'SKIPPED' ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={onUnskip}>
            {t('onboarding:actions.unskip')}
          </Button>
        ) : null}
      </span>
    </li>
  );
}
